/**
 * DRE (Income Statement) Performance Dashboard
 * Uses REAL data from DRE API
 */

import React, { useMemo, useState } from 'react'
import { DollarSign, TrendingDown, Receipt, Calculator, PieChart, Activity, Calendar } from 'lucide-react'
import {
  AnalyticsPerformanceDashboard,
  type MetricDefinition,
  type TimeSeriesDataPoint,
  type DetailView
} from '../AnalyticsPerformanceDashboard'
import type { ColumnDefinition } from '../AnalyticsDetailTable'
import type { FilterDimension } from '../AnalyticsFilters'
import { useDREData } from '@/app/financial/dre/hooks/useDREData'

/**
 * Format currency in Brazilian Real
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2
  }).format(value)
}

/**
 * Format percentage
 */
function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`
}

/**
 * Format number with thousands separator
 */
function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value)
}

// Helper function to fill missing months with empty data
function fillMissingMonths(
  monthlyValues: number[],
  year: number,
  upToMonth?: number
): Array<{ month: string; value: number }> {
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  const maxMonth = upToMonth || 12
  const filledData: Array<{ month: string; value: number }> = []

  for (let i = 0; i < maxMonth; i++) {
    const monthName = monthNames[i]
    const value = monthlyValues[i] !== undefined ? monthlyValues[i] : 0

    if (value === 0 && monthlyValues[i] === undefined) {
      console.log(`[DRE Dashboard] Filling missing month: ${monthName}/${year}`)
    }

    filledData.push({
      month: monthName,
      value
    })
  }

  return filledData
}

export function DREPerformanceDashboard() {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1 // 1-12

  // Filter state
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({})

  // WORKAROUND: Use current year data for metrics display
  // Yearly mode may not return monthlyValues arrays properly yet
  const { data: currentYearData, isLoading: loadingCurrent, error: errorCurrent } = useDREData({
    mode: 'yearly',
    year: currentYear
  })
  const { data: previousYearData, isLoading: loadingPrevious, error: errorPrevious } = useDREData({
    mode: 'yearly',
    year: currentYear - 1
  })

  const isLoading = loadingCurrent || loadingPrevious
  const error = errorCurrent || errorPrevious
  const dreData = currentYearData

  console.log('[DRE Dashboard] Current Year Full Data:', currentYearData)
  console.log('[DRE Dashboard] Previous Year Full Data:', previousYearData)

  const metrics: MetricDefinition[] = useMemo(() => {
    if (!dreData?.calculations) return []

    const calc = dreData.calculations

    return [
      {
        key: 'receitaBruta',
        label: 'Receita Bruta',
        value: `R$ ${(calc.receitaBruta / 1000000).toFixed(1)}M`,
        change: calc.margemBruta ? `${calc.margemBruta.toFixed(1)}%` : 'N/A',
        icon: DollarSign,
        color: 'brand-primary',
        useNormalization: false // Show raw values
      },
      {
        key: 'receitaLiquida',
        label: 'Receita Líquida',
        value: `R$ ${(calc.receitaLiquida / 1000000).toFixed(1)}M`,
        icon: PieChart,
        color: 'brand-blue',
        useNormalization: false // Show raw values
      },
      {
        key: 'lucroBruto',
        label: 'Lucro Bruto',
        value: `R$ ${(calc.lucroBruto / 1000000).toFixed(1)}M`,
        icon: Activity,
        color: 'success',
        useNormalization: false // Show raw values
      },
      {
        key: 'lucroLiquido',
        label: 'Lucro Líquido',
        value: `R$ ${(calc.lucroLiquido / 1000000).toFixed(1)}M`,
        change: calc.margemLiquida ? `${calc.margemLiquida.toFixed(1)}%` : 'N/A',
        icon: Calculator,
        color: 'success',
        useNormalization: false // Show raw values
      }
    ]
  }, [dreData])

  const timeSeriesData: TimeSeriesDataPoint[] = useMemo(() => {
    // Combine data from both years to get 24 months of history
    const combinedData: TimeSeriesDataPoint[] = []

    console.log('[DRE Dashboard] Current Year Data:', currentYearData?.lineItems?.length)
    console.log('[DRE Dashboard] Previous Year Data:', previousYearData?.lineItems?.length)
    console.log('[DRE Dashboard] Current Month:', currentMonth)

    // Helper to extract metrics from DRE line items
    const extractMetrics = (data: any) => {
      if (!data?.lineItems || !Array.isArray(data.lineItems)) {
        console.log('[DRE Dashboard] No lineItems found in data')
        return null
      }

      console.log('[DRE Dashboard] Line items sample:', data.lineItems.slice(0, 3))

      const yearlyItems = data.lineItems.filter((item: any) => 'monthlyValues' in item) as any[]
      console.log('[DRE Dashboard] Yearly items with monthlyValues:', yearlyItems.length)

      if (yearlyItems.length > 0) {
        console.log('[DRE Dashboard] First yearly item:', yearlyItems[0])
      }

      // Try multiple strategies to find revenue and cost data
      const receitaItem = yearlyItems.find((item) =>
        item.label?.toLowerCase().includes('receita bruta') ||
        item.label?.toLowerCase().includes('receita líquida') ||
        item.code === 'RECEITA_BRUTA' ||
        item.code === 'RECEITA_LIQUIDA' ||
        item.type === 'revenue'
      )

      const custoItem = yearlyItems.find((item) =>
        item.label?.toLowerCase().includes('custo') ||
        item.label?.toLowerCase().includes('despesa') ||
        item.code === 'CUSTO_SERVICOS' ||
        item.type === 'expense'
      )

      console.log('[DRE Dashboard] Found receita item:', receitaItem?.label || receitaItem?.code)
      console.log('[DRE Dashboard] Found custo item:', custoItem?.label || custoItem?.code)

      return {
        receitaValues: receitaItem?.monthlyValues || [],
        custoValues: custoItem?.monthlyValues || []
      }
    }

    // Add previous year data (fill missing months with zeros)
    if (previousYearData) {
      const metrics = extractMetrics(previousYearData)
      if (metrics) {
        const filledReceita = fillMissingMonths(metrics.receitaValues, currentYear - 1, 12)
        const filledCusto = fillMissingMonths(metrics.custoValues, currentYear - 1, 12)

        console.log('[DRE Dashboard] Adding', filledReceita.length, 'months from previous year (gaps filled)')

        filledReceita.forEach((item, index) => {
          const receitaBruta = item.value
          const custos = filledCusto[index].value

          combinedData.push({
            date: `${item.month}/${currentYear - 1}`, // Add year to tooltip
            receitaBruta,
            receitaLiquida: receitaBruta * 0.9,
            lucroBruto: receitaBruta - custos,
            lucroLiquido: (receitaBruta - custos) * 0.8
          })
        })
      }
    }

    // Add current year data (fill missing months up to current month)
    if (currentYearData) {
      const metrics = extractMetrics(currentYearData)
      if (metrics) {
        const filledReceita = fillMissingMonths(metrics.receitaValues, currentYear, currentMonth)
        const filledCusto = fillMissingMonths(metrics.custoValues, currentYear, currentMonth)

        console.log('[DRE Dashboard] Adding', filledReceita.length, 'months from current year (gaps filled)')

        filledReceita.forEach((item, index) => {
          const receitaBruta = item.value
          const custos = filledCusto[index].value

          combinedData.push({
            date: `${item.month}/${currentYear}`, // Add year to tooltip
            receitaBruta,
            receitaLiquida: receitaBruta * 0.9,
            lucroBruto: receitaBruta - custos,
            lucroLiquido: (receitaBruta - custos) * 0.8
          })
        })
      }
    }

    console.log('[DRE Dashboard] Total combined months:', combinedData.length)
    console.log('[DRE Dashboard] Date range:', combinedData[0]?.date, 'to', combinedData[combinedData.length - 1]?.date)

    return combinedData
  }, [currentYearData, previousYearData, currentMonth])

  // Detail views for by-month breakdown
  const byMonth = useMemo(() => {
    return timeSeriesData.map((item, index) => ({
      month: item.date,
      monthNumber: index + 1,
      receitaBruta: item.receitaBruta,
      receitaLiquida: item.receitaLiquida,
      lucroBruto: item.lucroBruto,
      lucroLiquido: item.lucroLiquido
    }))
  }, [timeSeriesData])

  const monthColumns: ColumnDefinition[] = [
    {
      key: 'month',
      label: 'Mês',
      sortable: false,
      align: 'left'
    },
    {
      key: 'receitaBruta',
      label: 'Receita Bruta',
      sortable: true,
      align: 'right',
      render: (value: number) => (
        <span className="font-semibold text-white">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'receitaLiquida',
      label: 'Receita Líquida',
      sortable: true,
      align: 'right',
      render: (value: number) => formatCurrency(value),
      className: 'text-brand-gray'
    },
    {
      key: 'lucroBruto',
      label: 'Lucro Bruto',
      sortable: true,
      align: 'right',
      render: (value: number) => (
        <span className={value >= 0 ? 'text-success' : 'text-error'}>
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'lucroLiquido',
      label: 'Lucro Líquido',
      sortable: true,
      align: 'right',
      render: (value: number) => (
        <span className={value >= 0 ? 'text-success' : 'text-error'}>
          {formatCurrency(value)}
        </span>
      )
    }
  ]

  // Detail Views
  const detailViews: DetailView[] = [
    {
      id: 'by-month',
      label: 'Por Mês',
      icon: Calendar,
      columns: monthColumns,
      data: byMonth,
      keyExtractor: (row) => row.monthNumber.toString(),
      defaultSortBy: 'monthNumber',
      defaultSortOrder: 'asc',
      emptyMessage: 'Nenhum dado mensal disponível'
    }
  ]

  // Handle filter changes
  const handleFilterChange = (filterKey: string, values: string[]) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterKey]: values
    }))
  }

  const handleClearFilters = () => {
    setActiveFilters({})
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-brand-gray">Carregando DRE...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-error">Erro ao carregar DRE: {error.message}</div>
      </div>
    )
  }

  if (!dreData || metrics.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-brand-gray">Nenhum dado DRE disponível</div>
      </div>
    )
  }

  return (
    <AnalyticsPerformanceDashboard
      metrics={metrics}
      timeSeriesData={timeSeriesData}
      title="DRE - Demonstração do Resultado"
      dateRange={`${currentYear - 1} - ${currentYear} (até ${new Date().toLocaleDateString('pt-BR', { month: 'long' })})`}
      enableComparison={true}
      detailViews={detailViews}
      defaultDetailView="by-month"
      onFilterChange={handleFilterChange}
    />
  )
}
